// src/pages/admin/ViewEventRegister.tsx
//
// Two-tab admin view for an event:
//   Tab 1 — All Registrations  (original implementation preserved exactly)
//   Tab 2 — Attendees           (attendance analytics + comparative analysis)

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import {
  Search,
  FileText,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Trash2,
  CheckCircle2,
  Clock,
  Users,
  MapPin,
  Loader2,
  BarChart2,
  TrendingUp,
  X,
  FileSpreadsheet,
  Building2,
  ArrowUpDown,
  Mail,
  AlertCircle,
  Award,
  UserCheck,
} from "lucide-react";
import {
  useGetEventRegistrationsQuery,
  useGetEventAttendeesQuery,
  useUpdateRegistrationStatusMutation,
  useDeleteRegistrationMutation,
  useSendCertificatesMutation,
  useGetCertificateJobStatusQuery,
  type EventRegistration,
  type ProvinceDistribution,
  type ComparisonRow,
  type SortBy,
} from "../../app/api/eventApi";
import { useAppSelector } from "../../app/hooks";
import { staticEvent } from "../../data/staticEvent";

/* ─────────────────────────────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────────────────────────────── */

const COLOURS = [
  "#166534",
  "#15803d",
  "#16a34a",
  "#22c55e",
  "#4ade80",
  "#065f46",
  "#047857",
  "#059669",
  "#10b981",
  "#34d399",
  "#1e40af",
  "#1d4ed8",
  "#2563eb",
  "#3b82f6",
  "#60a5fa",
  "#7c3aed",
  "#6d28d9",
  "#8b5cf6",
  "#a78bfa",
  "#c4b5fd",
  "#b45309",
  "#d97706",
  "#f59e0b",
  "#fbbf24",
  "#fcd34d",
  "#be123c",
  "#e11d48",
  "#f43f5e",
  "#fb7185",
  "#fda4af",
];

const REGION_BASE_COLOURS = [
  "#7c3aed",
  "#166534",
  "#1e40af",
  "#b45309",
  "#be123c",
  "#065f46",
  "#1d4ed8",
  "#92400e",
  "#6d28d9",
  "#047857",
];

const STATUS_COLOURS: Record<string, string> = {
  registered: "#16a34a",
  waitlisted: "#d97706",
  attended: "#2563eb",
  cancelled: "#dc2626",
};

const STATUS_OPTIONS = ["registered", "waitlisted", "attended", "cancelled"];

const SORT_OPTIONS: { value: SortBy; label: string }[] = [
  { value: "province", label: "Province (primary)" },
  { value: "region", label: "Region" },
  { value: "createdAt", label: "Registration date" },
];

/* ─────────────────────────────────────────────────────────────────
   TYPES & GROUPED DATA BUILDER
   Provinces that have no provinceName fall back to their regionName
   in the display label — this keeps the bar chart and grouped table
   readable without losing the region context.
───────────────────────────────────────────────────────────────── */

interface ProvinceRow {
  province: string;
  region: string;
  count: number;
  colour: string;
}

interface RegionGroup {
  region: string;
  regionColour: string;
  totalCount: number;
  provinces: ProvinceRow[];
}

function buildRegionGroups(raw: ProvinceDistribution[]): RegionGroup[] {
  const map = new Map<string, ProvinceDistribution[]>();
  raw.forEach((p) => {
    const key = p.region || p.province || "Unassigned";
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(p);
  });
  const groups: RegionGroup[] = [];
  Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .forEach(([region, provinces], rIdx) => {
      const regionColour =
        REGION_BASE_COLOURS[rIdx % REGION_BASE_COLOURS.length];
      const sorted = [...provinces].sort((a, b) => b.count - a.count);
      groups.push({
        region,
        regionColour,
        totalCount: sorted.reduce((s, p) => s + p.count, 0),
        provinces: sorted.map((p) => ({
          province: p.province || p.region || region,
          region,
          count: p.count,
          colour: regionColour,
        })),
      });
    });
  return groups;
}

/* ─────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────── */

const fmtDate = (d: string) =>
  new Date(d).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const Bone: React.FC<{ className?: string }> = ({ className = "" }) => (
  <div className={`animate-pulse rounded-xl bg-gray-100 ${className}`} />
);

/* ─────────────────────────────────────────────────────────────────
   EXPORT HELPERS
───────────────────────────────────────────────────────────────── */

async function exportRegistrationsToExcel(
  registrations: EventRegistration[],
  eventName: string,
) {
  const XLSX = await import("xlsx");
  const rows = registrations.map((r) => ({
    "Confirmation Code": r.confirmationCode,
    "First Name": r.firstName,
    "Last Name": r.lastName,
    Email: r.email,
    Phone: r.phoneNo ?? "",
    Province: r.provinceName ?? "",
    Region: r.region,
    Status: r.status,
    "Registered At": fmtDate(r.createdAt),
  }));
  const ws = XLSX.utils.json_to_sheet(rows);
  ws["!cols"] = [
    { wch: 18 },
    { wch: 16 },
    { wch: 16 },
    { wch: 30 },
    { wch: 18 },
    { wch: 20 },
    { wch: 20 },
    { wch: 14 },
    { wch: 22 },
  ];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Registrations");
  XLSX.writeFile(
    wb,
    `${eventName.replace(/\s+/g, "-").toLowerCase()}-registrations.xlsx`,
  );
}

async function exportAttendeesToExcel(
  registrations: EventRegistration[],
  eventName: string,
) {
  const XLSX = await import("xlsx");
  const rows = registrations.map((r) => ({
    "Confirmation Code": r.confirmationCode,
    "First Name": r.firstName,
    "Last Name": r.lastName,
    Email: r.email,
    Phone: r.phoneNo ?? "",
    Province: r.provinceName ?? "",
    Region: r.region,
    "Certificate Sent": r.hasSentCertificate ? "Yes" : "No",
    "Registered At": fmtDate(r.createdAt),
  }));
  const ws = XLSX.utils.json_to_sheet(rows);
  ws["!cols"] = [
    { wch: 18 },
    { wch: 16 },
    { wch: 16 },
    { wch: 30 },
    { wch: 18 },
    { wch: 20 },
    { wch: 20 },
    { wch: 16 },
    { wch: 22 },
  ];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Attendees");
  XLSX.writeFile(
    wb,
    `${eventName.replace(/\s+/g, "-").toLowerCase()}-attendees.xlsx`,
  );
}

async function exportProvinceBreakdownToExcel(
  groups: RegionGroup[],
  eventName: string,
  total: number,
) {
  const XLSX = await import("xlsx");
  const rows: any[] = [];
  groups.forEach((g) => {
    rows.push({
      Region: g.region,
      Province: "(all provinces)",
      Count: g.totalCount,
      "% of Total":
        total > 0 ? `${((g.totalCount / total) * 100).toFixed(1)}%` : "0.0%",
    });
    g.provinces.forEach((p) => {
      rows.push({
        Region: g.region,
        Province: p.province,
        Count: p.count,
        "% of Total":
          total > 0 ? `${((p.count / total) * 100).toFixed(1)}%` : "0.0%",
      });
    });
    rows.push({ Region: "", Province: "", Count: "", "% of Total": "" });
  });
  const ws = XLSX.utils.json_to_sheet(rows);
  ws["!cols"] = [{ wch: 28 }, { wch: 28 }, { wch: 10 }, { wch: 12 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "By Region & Province");
  XLSX.writeFile(
    wb,
    `${eventName.replace(/\s+/g, "-").toLowerCase()}-province-breakdown.xlsx`,
  );
}

async function exportProvinceBreakdownToPDF(
  groups: RegionGroup[],
  eventName: string,
  total: number,
) {
  const { jsPDF } = await import("jspdf");
  const autoTable = (await import("jspdf-autotable")).default;
  const doc = new (jsPDF as any)({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });
  doc.setFontSize(15);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(22, 101, 52);
  doc.text("Registrations by Region & Province", 14, 18);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(100, 100, 100);
  doc.text(`Event: ${eventName}`, 14, 26);
  doc.text(
    `${total} total registrations  ·  Exported ${new Date().toLocaleString("en-GB")}`,
    14,
    32,
  );
  const body: any[] = [];
  groups.forEach((g) => {
    body.push([
      {
        content: g.region,
        styles: { fontStyle: "bold", fillColor: [237, 233, 254] },
      },
      {
        content: "All provinces",
        styles: { fontStyle: "bold", fillColor: [237, 233, 254] },
      },
      {
        content: g.totalCount,
        styles: { fontStyle: "bold", fillColor: [237, 233, 254] },
      },
      {
        content:
          total > 0 ? `${((g.totalCount / total) * 100).toFixed(1)}%` : "—",
        styles: { fontStyle: "bold", fillColor: [237, 233, 254] },
      },
    ]);
    g.provinces.forEach((p) => {
      body.push([
        "",
        `    ${p.province}`,
        p.count,
        total > 0 ? `${((p.count / total) * 100).toFixed(1)}%` : "—",
      ]);
    });
  });
  autoTable(doc, {
    startY: 38,
    head: [["Region", "Province", "Count", "% of Total"]],
    body,
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: { fillColor: [22, 101, 52], textColor: 255, fontStyle: "bold" },
    columnStyles: {
      0: { cellWidth: 55 },
      1: { cellWidth: 80 },
      2: { cellWidth: 20 },
      3: { cellWidth: 22 },
    },
  });
  doc.save(
    `${eventName.replace(/\s+/g, "-").toLowerCase()}-province-breakdown.pdf`,
  );
}

async function exportFullToPDF(
  registrations: EventRegistration[],
  eventName: string,
  groups: RegionGroup[],
  total: number,
) {
  const { jsPDF } = await import("jspdf");
  const autoTable = (await import("jspdf-autotable")).default;
  const doc = new (jsPDF as any)({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(22, 101, 52);
  doc.text(eventName, 14, 18);
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(100, 100, 100);
  doc.text(`Exported ${new Date().toLocaleString("en-GB")}`, 14, 26);
  doc.text(`Total registrations: ${registrations.length}`, 14, 32);
  autoTable(doc, {
    startY: 40,
    head: [
      [
        "Code",
        "First Name",
        "Last Name",
        "Email",
        "Phone",
        "Province",
        "Region",
        "Status",
        "Registered",
      ],
    ],
    body: registrations.map((r) => [
      r.confirmationCode,
      r.firstName,
      r.lastName,
      r.email,
      r.phoneNo ?? "",
      r.provinceName ?? "",
      r.region,
      r.status,
      fmtDate(r.createdAt),
    ]),
    styles: { fontSize: 7, cellPadding: 2 },
    headStyles: { fillColor: [22, 101, 52], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [240, 253, 244] },
    columnStyles: { 3: { cellWidth: 42 } },
  });
  if (groups.length > 0) {
    doc.addPage();
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(22, 101, 52);
    doc.text("Registrations by Region & Province", 14, 18);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 100, 100);
    doc.text(`${total} total registrations`, 14, 26);
    const body: any[] = [];
    groups.forEach((g) => {
      body.push([
        {
          content: g.region,
          styles: { fontStyle: "bold", fillColor: [237, 233, 254] },
        },
        {
          content: "All provinces",
          styles: { fontStyle: "bold", fillColor: [237, 233, 254] },
        },
        {
          content: g.totalCount,
          styles: { fontStyle: "bold", fillColor: [237, 233, 254] },
        },
        {
          content:
            total > 0 ? `${((g.totalCount / total) * 100).toFixed(1)}%` : "—",
          styles: { fontStyle: "bold", fillColor: [237, 233, 254] },
        },
      ]);
      g.provinces.forEach((p) => {
        body.push([
          "",
          `  ${p.province}`,
          p.count,
          total > 0 ? `${((p.count / total) * 100).toFixed(1)}%` : "—",
        ]);
      });
    });
    autoTable(doc, {
      startY: 32,
      head: [["Region", "Province", "Count", "% of Total"]],
      body,
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: {
        fillColor: [22, 101, 52],
        textColor: 255,
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: 80 },
        2: { cellWidth: 20 },
        3: { cellWidth: 24 },
      },
    });
  }
  doc.save(`${eventName.replace(/\s+/g, "-").toLowerCase()}-registrations.pdf`);
}

/* ─────────────────────────────────────────────────────────────────
   CERTIFICATE PROGRESS BANNER
───────────────────────────────────────────────────────────────── */

const CertificateProgressBanner: React.FC<{
  eventSlug: string;
  jobId: string;
  onDone: () => void;
  onDismiss: () => void;
}> = ({ eventSlug, jobId, onDone, onDismiss }) => {
  const { data, isError } = useGetCertificateJobStatusQuery(
    { eventSlug, jobId },
    { pollingInterval: 4_000, skip: false },
  );

  useEffect(() => {
    if (data?.status === "done" || data?.status === "failed") {
      onDone();
    }
  }, [data?.status, onDone]);

  const isDone = data?.status === "done";
  const isFailed = data?.status === "failed";
  const isRunning = !data || data.status === "running";
  const pct =
    data && data.total > 0
      ? Math.round(((data.sent + data.failed) / data.total) * 100)
      : 0;
  const etaText =
    data?.etaSeconds != null && isRunning
      ? data.etaSeconds < 60
        ? `~${data.etaSeconds}s remaining`
        : `~${Math.ceil(data.etaSeconds / 60)}min remaining`
      : null;

  return (
    <div
      className={`rounded-2xl border p-4 transition-all ${isDone ? "bg-green-50 border-green-200" : isFailed ? "bg-red-50 border-red-200" : "bg-blue-50 border-blue-200"}`}
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 mt-0.5">
          {isRunning && (
            <Loader2 size={18} className="animate-spin text-blue-600" />
          )}
          {isDone && <CheckCircle2 size={18} className="text-green-600" />}
          {isFailed && <AlertCircle size={18} className="text-red-600" />}
        </div>
        <div className="flex-1 min-w-0">
          <p
            className={`text-sm font-bold ${isDone ? "text-green-800" : isFailed ? "text-red-800" : "text-blue-800"}`}
          >
            {isRunning && "Sending certificates…"}
            {isDone && "Certificates sent successfully"}
            {isFailed && "Certificate send failed"}
          </p>
          {data && (
            <p className="text-xs text-gray-600 mt-0.5">
              {data.sent} sent · {data.failed} failed · {data.total} total
              {etaText && <span className="ml-2 text-gray-400">{etaText}</span>}
            </p>
          )}
          {isError && (
            <p className="text-xs text-red-600 mt-0.5">
              Lost connection to job status — the send is still running in the
              background.
            </p>
          )}
          {isRunning && data && (
            <div className="mt-2 h-1.5 w-full bg-blue-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 rounded-full transition-all duration-500"
                style={{ width: `${pct}%` }}
              />
            </div>
          )}
          {(isDone || isFailed) && data && data.failed > 0 && (
            <details className="mt-2">
              <summary className="text-xs font-semibold text-red-700 cursor-pointer select-none">
                {data.failed} failed — click to see
              </summary>
              <ul className="mt-1.5 space-y-1 max-h-32 overflow-y-auto">
                {data.failedList.slice(0, 20).map((f) => (
                  <li key={f.id} className="text-[11px] text-red-700 font-mono">
                    {f.email} — {f.error}
                  </li>
                ))}
                {data.failedList.length > 20 && (
                  <li className="text-[11px] text-red-400">
                    +{data.failedList.length - 20} more
                  </li>
                )}
              </ul>
            </details>
          )}
        </div>
        {(isDone || isFailed) && (
          <button
            onClick={onDismiss}
            className="shrink-0 p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-white/60 transition-colors"
          >
            <X size={14} />
          </button>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   STAT CARD
───────────────────────────────────────────────────────────────── */

const StatCard: React.FC<{
  label: string;
  value: string | number;
  sub?: string;
  colour: string;
  icon: React.ReactNode;
}> = ({ label, value, sub, colour, icon }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4">
    <div className="flex items-center gap-2 mb-2">
      <span style={{ color: colour }}>{icon}</span>
      <p className="text-xs text-gray-400 font-medium">{label}</p>
    </div>
    <p className="text-2xl font-black" style={{ color: colour }}>
      {value}
    </p>
    {sub && <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>}
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   GROUPED PROVINCE CHART
   Shared between both tabs. Regions that have no province data show
   their region name as the province label (the fallback is applied
   by buildRegionGroups before this component receives data).
───────────────────────────────────────────────────────────────── */

const GroupedProvinceChart: React.FC<{
  groups: RegionGroup[];
  total: number;
  eventName: string;
  accentColour?: string;
  label?: string;
}> = ({
  groups,
  total,
  eventName,
  accentColour = "#7c3aed",
  label = "Registrations by Region & Province",
}) => {
  const [exportingChart, setExportingChart] = useState<"excel" | "pdf" | null>(
    null,
  );
  const handleExport = async (format: "excel" | "pdf") => {
    setExportingChart(format);
    try {
      if (format === "excel")
        await exportProvinceBreakdownToExcel(groups, eventName, total);
      else await exportProvinceBreakdownToPDF(groups, eventName, total);
    } catch (e) {
      console.error("Province chart export failed:", e);
      alert("Export failed. Make sure xlsx and jspdf packages are installed.");
    } finally {
      setExportingChart(null);
    }
  };
  const flatData = groups.flatMap((g) => g.provinces);
  if (groups.length === 0)
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <p className="text-xs font-semibold text-gray-700 mb-4 flex items-center gap-1.5">
          <Building2 size={13} style={{ color: accentColour }} />
          {label}
        </p>
        <div className="h-32 flex items-center justify-center text-sm text-gray-300">
          No province data yet
        </div>
      </div>
    );
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
            <Building2 size={13} style={{ color: accentColour }} />
            {label}
          </p>
          <p className="text-[11px] text-gray-400 mt-0.5">
            {groups.length} region{groups.length !== 1 ? "s" : ""} ·{" "}
            {flatData.length} province{flatData.length !== 1 ? "s" : ""} ·{" "}
            {total} total
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => handleExport("excel")}
            disabled={!!exportingChart}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-xl border border-green-200 text-green-700 hover:bg-green-50 disabled:opacity-50 transition-colors"
          >
            {exportingChart === "excel" ? (
              <Loader2 size={11} className="animate-spin" />
            ) : (
              <FileSpreadsheet size={11} />
            )}{" "}
            Excel
          </button>
          <button
            onClick={() => handleExport("pdf")}
            disabled={!!exportingChart}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-xl border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50 transition-colors"
          >
            {exportingChart === "pdf" ? (
              <Loader2 size={11} className="animate-spin" />
            ) : (
              <FileText size={11} />
            )}{" "}
            PDF
          </button>
        </div>
      </div>
      <div className="overflow-hidden rounded-xl border border-gray-100">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              {["Region", "Province", "Count", "% Total"].map((h, i) => (
                <th
                  key={h}
                  className={`px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest text-gray-400 ${i >= 2 ? "text-right" : "text-left"}`}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {groups.map((g) => (
              <React.Fragment key={g.region}>
                <tr className="bg-indigo-50/60">
                  <td className="px-4 py-2 font-bold text-gray-800">
                    <div className="flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-sm shrink-0"
                        style={{ background: g.regionColour }}
                      />
                      {g.region}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-gray-400 italic text-[11px]">
                    {g.provinces.length} province
                    {g.provinces.length !== 1 ? "s" : ""}
                  </td>
                  <td className="px-4 py-2 text-right font-bold text-gray-800">
                    {g.totalCount}
                  </td>
                  <td className="px-4 py-2 text-right font-semibold text-gray-500">
                    {total > 0
                      ? `${((g.totalCount / total) * 100).toFixed(1)}%`
                      : "—"}
                  </td>
                </tr>
                {g.provinces.map((p) => (
                  <tr
                    key={`${g.region}-${p.province}`}
                    className="hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="px-4 py-2 text-gray-300 text-[10px] pl-8">
                      └
                    </td>
                    <td className="px-4 py-2 text-gray-700 font-medium pl-2">
                      <div className="flex items-center gap-1.5">
                        <Building2
                          size={10}
                          className="text-gray-300 shrink-0"
                        />
                        {p.province}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right text-gray-700 font-semibold">
                      {p.count}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-400">
                      {total > 0
                        ? `${((p.count / total) * 100).toFixed(1)}%`
                        : "—"}
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-200 bg-gray-50">
              <td className="px-4 py-2.5 font-bold text-gray-900" colSpan={2}>
                Grand Total
              </td>
              <td className="px-4 py-2.5 text-right font-bold text-gray-900">
                {total}
              </td>
              <td className="px-4 py-2.5 text-right font-bold text-gray-900">
                100%
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   ATTENDEES TAB
   Province bar chart and grouped table use the same cleanedProvinceChart
   / buildRegionGroups approach as the Registrations tab — regions
   without a province carry their region name as the province label.
   The comparative analysis table uses byComparison from the backend
   which correctly labels these as "No province recorded".
───────────────────────────────────────────────────────────────── */

const AttendeesTab: React.FC<{
  eventSlug: string;
  eventName: string;
  isAdmin: boolean;
  activeJobId: string | null;
  sendStarting: boolean;
  onSendCertificates: () => void;
}> = ({
  eventSlug,
  eventName,
  isAdmin,
  activeJobId,
  sendStarting,
  onSendCertificates,
}) => {
  const [search, setSearch] = useState("");
  const [regionFilter, setRegionFilter] = useState("");
  const [provinceFilter, setProvinceFilter] = useState("");
  const [certFilter, setCertFilter] = useState("");
  const [sortBy, setSortBy] = useState<SortBy>("province");
  const [page, setPage] = useState(1);
  const [showCharts, setShowCharts] = useState(true);
  const [exporting, setExporting] = useState<"excel" | "pdf" | null>(null);
  const [compSort, setCompSort] = useState<"registered" | "attended" | "rate">(
    "registered",
  );

  const hasSentCertFilter =
    certFilter === "sent" ? true : certFilter === "pending" ? false : undefined;

  const {
    data: attRes,
    isLoading,
    isFetching,
    refetch,
  } = useGetEventAttendeesQuery({
    eventSlug,
    regionName: regionFilter || undefined,
    provinceName: provinceFilter || undefined,
    search: search.trim() || undefined,
    hasSentCertificate: hasSentCertFilter,
    sortBy,
    page,
    limit: 50,
  });

  // Unfiltered fetch — charts always show full picture
  const { data: chartRes } = useGetEventAttendeesQuery({
    eventSlug,
    limit: 1,
    page: 1,
  });
  const { data: allAttRes } = useGetEventAttendeesQuery({
    eventSlug,
    limit: 10_000,
    page: 1,
  });

  const attendees = attRes?.data ?? [];
  const total = attRes?.total ?? 0;
  const totalPages = attRes?.pages ?? 1;
  const totalAttended = chartRes?.totalAttended ?? 0;
  const certSentCount = chartRes?.certificateSentCount ?? 0;
  const certPending = totalAttended - certSentCount;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const regionChart = chartRes?.charts?.byRegion ?? [];
  // byProvince already has regionName fallback applied by the backend —
  // identical shape to the registrations byProvince. Use directly.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const provinceChart = (chartRes?.charts?.byProvince ??
    []) as ProvinceDistribution[];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const comparisonRaw = (chartRes?.charts?.byComparison ??
    []) as ComparisonRow[];

  const allAttendees = allAttRes?.data ?? [];
  const grandTotal = chartRes?.totalAttended ?? totalAttended;

  // Identical to RegistrationsTab: fall back to region name for bar chart display
  const cleanedProvinceChart = useMemo(
    () =>
      provinceChart.map((p) => ({
        ...p,
        province:
          !p.province || p.province === "Unknown" ? p.region : p.province,
      })),
    [provinceChart],
  );

  const regionGroups = useMemo(
    () => buildRegionGroups(cleanedProvinceChart),
    [cleanedProvinceChart],
  );

  const allRegions = useMemo(
    () => Array.from(new Set(regionChart.map((r) => r.region))).sort(),
    [regionChart],
  );
  // Province dropdown: exclude entries where province === region (fallback entries)
  // so only real province names appear as filter options
  const allProvinces = useMemo(
    (): string[] =>
      Array.from(
        new Set(
          provinceChart
            .filter(
              (p): p is ProvinceDistribution & { province: string } =>
                typeof p.province === "string" &&
                p.province !== "" &&
                p.province !== p.region,
            )
            .map((p) => p.province),
        ),
      ).sort(),
    [provinceChart],
  );

  // Sorted comparison table
  const sortedComparison = useMemo(
    () =>
      [...comparisonRaw].sort((a, b) => {
        if (compSort === "rate") return b.attendanceRate - a.attendanceRate;
        if (compSort === "attended") return b.attended - a.attended;
        return b.registered - a.registered;
      }),
    [comparisonRaw, compSort],
  );

  const totalRegistered = useMemo(
    () => comparisonRaw.reduce((s, r) => s + r.registered, 0),
    [comparisonRaw],
  );
  const overallRate =
    totalRegistered > 0
      ? ((totalAttended / totalRegistered) * 100).toFixed(1)
      : "0.0";
  const highestRateProvince = useMemo(
    () =>
      [...comparisonRaw]
        .filter((r) => r.province !== null && r.registered >= 5)
        .sort((a, b) => b.attendanceRate - a.attendanceRate)[0],
    [comparisonRaw],
  );

  const certDonutData = [
    { name: "Sent", value: certSentCount, fill: "#16a34a" },
    { name: "Pending", value: certPending, fill: "#d1d5db" },
  ].filter((d) => d.value > 0);
  const certPct =
    totalAttended > 0 ? Math.round((certSentCount / totalAttended) * 100) : 0;

  const rateColour = (rate: number) =>
    rate >= 75
      ? "#16a34a"
      : rate >= 50
        ? "#2563eb"
        : rate >= 25
          ? "#d97706"
          : "#dc2626";
  const rateLabel = (rate: number) =>
    rate >= 75 ? "High" : rate >= 50 ? "Good" : rate >= 25 ? "Low" : "Very low";

  const hasFilters = !!(regionFilter || provinceFilter || search || certFilter);
  const resetFilters = () => {
    setSearch("");
    setRegionFilter("");
    setProvinceFilter("");
    setCertFilter("");
    setPage(1);
  };

  const handleExport = async (format: "excel" | "pdf") => {
    setExporting(format);
    try {
      if (format === "excel") {
        await exportAttendeesToExcel(allAttendees, eventName);
      } else {
        await exportFullToPDF(
          allAttendees,
          eventName,
          regionGroups,
          grandTotal,
        );
      }
    } catch (e) {
      alert("Export failed. Make sure xlsx and jspdf packages are installed.");
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* ── ACTION ROW ── */}
      <div className="flex items-center gap-2 flex-wrap justify-end">
        <button
          onClick={() => handleExport("excel")}
          disabled={!!exporting || allAttendees.length === 0}
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl border border-green-200 text-green-700 hover:bg-green-50 disabled:opacity-50 transition-colors"
        >
          {exporting === "excel" ? (
            <Loader2 size={12} className="animate-spin" />
          ) : (
            <FileSpreadsheet size={12} />
          )}{" "}
          Export Excel
        </button>
        <button
          onClick={() => handleExport("pdf")}
          disabled={!!exporting || allAttendees.length === 0}
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50 transition-colors"
        >
          {exporting === "pdf" ? (
            <Loader2 size={12} className="animate-spin" />
          ) : (
            <FileText size={12} />
          )}{" "}
          Export PDF
        </button>
        {isAdmin && (
          <button
            onClick={onSendCertificates}
            disabled={sendStarting || !!activeJobId || certPending === 0}
            title={
              certPending === 0
                ? "All attendees have received certificates"
                : `Send to ${certPending} pending attendee${certPending !== 1 ? "s" : ""}`
            }
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 active:scale-[0.98] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-white transition-all"
          >
            {sendStarting ? (
              <>
                <Loader2 size={12} className="animate-spin" /> Starting…
              </>
            ) : activeJobId ? (
              <>
                <Loader2 size={12} className="animate-spin" /> Sending…
              </>
            ) : (
              <>
                <Mail size={12} /> Send Certificates
                {certPending > 0 ? ` (${certPending})` : ""}
              </>
            )}
          </button>
        )}
        <button
          onClick={() => refetch()}
          className="p-2 rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
        >
          {isFetching ? (
            <Loader2 size={13} className="animate-spin" />
          ) : (
            <RefreshCw size={13} />
          )}
        </button>
      </div>

      {/* ── STAT CARDS ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard
          label="Total attended"
          value={totalAttended.toLocaleString()}
          sub={`of ${totalRegistered.toLocaleString()} registered`}
          colour="#2563eb"
          icon={<UserCheck size={14} />}
        />
        <StatCard
          label="Overall attendance rate"
          value={`${overallRate}%`}
          sub="attended ÷ registered"
          colour={Number(overallRate) >= 50 ? "#16a34a" : "#d97706"}
          icon={<TrendingUp size={14} />}
        />
        <StatCard
          label="Certificates sent"
          value={certSentCount.toLocaleString()}
          sub={`${certPct}% of attendees`}
          colour="#16a34a"
          icon={<Award size={14} />}
        />
        <StatCard
          label="Pending certificates"
          value={certPending.toLocaleString()}
          sub="yet to receive"
          colour="#d97706"
          icon={<Clock size={14} />}
        />
      </div>

      {/* ── ANALYTICS TOGGLE ── */}
      <div className="flex items-center justify-between">
        <h2 className="text-xs font-bold uppercase tracking-widest text-gray-400">
          Attendance analytics
        </h2>
        <button
          onClick={() => setShowCharts((v) => !v)}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-gray-800 transition-colors"
        >
          <BarChart2 size={13} />
          {showCharts ? "Hide charts" : "Show charts"}
        </button>
      </div>

      {showCharts && (
        <div className="space-y-5">
          {/* ── Province bar + certificate donut + region mini-bars ── */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Province bar chart — same pattern as Registrations tab */}
            <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <p className="text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                <Building2 size={13} className="text-blue-600" /> Attendance by
                Province
              </p>
              <p className="text-[11px] text-gray-400 mb-4">
                {cleanedProvinceChart.length} province
                {cleanedProvinceChart.length !== 1 ? "s" : ""} represented
              </p>
              {cleanedProvinceChart.length === 0 ? (
                <div className="h-48 flex items-center justify-center text-sm text-gray-300">
                  No attendance data yet
                </div>
              ) : (
                <ResponsiveContainer
                  width="100%"
                  height={Math.max(200, cleanedProvinceChart.length * 28)}
                >
                  <BarChart data={cleanedProvinceChart} layout="vertical">
                    <CartesianGrid
                      strokeDasharray="3 3"
                      horizontal={false}
                      stroke="#f3f4f6"
                    />
                    <XAxis
                      type="number"
                      tick={{ fontSize: 10 }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="province"
                      width={110}
                      tick={{ fontSize: 10 }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        borderRadius: 10,
                        border: "1px solid #e5e7eb",
                        fontSize: 12,
                      }}
                      formatter={(v, _n, props) => [
                        `${Number(v)} attended`,
                        `${props.payload?.province || props.payload?.region}`,
                      ]}
                    />
                    <Bar
                      dataKey="count"
                      radius={[0, 6, 6, 0]}
                      maxBarSize={20}
                      label={{
                        position: "right",
                        fontSize: 10,
                        fill: "#6b7280",
                      }}
                    >
                      {cleanedProvinceChart.map((_, i) => (
                        <Cell key={i} fill={COLOURS[i % COLOURS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="flex flex-col gap-5">
              {/* Certificate donut */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <p className="text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                  <Award size={13} className="text-green-600" /> Certificate
                  Status
                </p>
                {certDonutData.length === 0 ? (
                  <div className="h-40 flex items-center justify-center text-sm text-gray-300">
                    No attendees yet
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <ResponsiveContainer width="100%" height={140}>
                      <PieChart>
                        <Pie
                          data={certDonutData}
                          dataKey="value"
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={62}
                          paddingAngle={3}
                        >
                          {certDonutData.map((entry, i) => (
                            <Cell key={i} fill={entry.fill} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            borderRadius: 10,
                            border: "1px solid #e5e7eb",
                            fontSize: 12,
                          }}
                          formatter={(v, name) => [
                            `${Number(v)} attendees`,
                            String(name),
                          ]}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="-mt-4 text-center">
                      <p className="text-xl font-black text-gray-900">
                        {certPct}%
                      </p>
                      <p className="text-[11px] text-gray-400">
                        certificates sent
                      </p>
                    </div>
                    <div className="flex items-center gap-4 mt-3">
                      <div className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-sm bg-green-600" />
                        <span className="text-[11px] text-gray-600">
                          Sent ({certSentCount})
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-sm bg-gray-300" />
                        <span className="text-[11px] text-gray-600">
                          Pending ({certPending})
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Region mini bars */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <p className="text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                  <TrendingUp size={13} className="text-blue-600" /> By Region
                </p>
                <p className="text-[11px] text-gray-400 mb-3">
                  {regionChart.length} region
                  {regionChart.length !== 1 ? "s" : ""}
                </p>
                {regionChart.length === 0 ? (
                  <div className="h-24 flex items-center justify-center text-sm text-gray-300">
                    No data yet
                  </div>
                ) : (
                  <div className="space-y-2">
                    {regionChart.slice(0, 8).map((r, i) => {
                      const max = regionChart[0]?.count ?? 1;
                      return (
                        <div key={r.region} className="flex items-center gap-2">
                          <p className="text-[10px] text-gray-600 w-20 truncate shrink-0">
                            {r.region}
                          </p>
                          <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                            <div
                              className="h-full rounded-full"
                              style={{
                                width: `${(r.count / max) * 100}%`,
                                background: COLOURS[i % COLOURS.length],
                              }}
                            />
                          </div>
                          <p className="text-[10px] text-gray-500 shrink-0 w-6 text-right">
                            {r.count}
                          </p>
                        </div>
                      );
                    })}
                    {regionChart.length > 8 && (
                      <p className="text-[10px] text-gray-400">
                        +{regionChart.length - 8} more regions
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Grouped province breakdown table (same as Registrations tab) ── */}
          <GroupedProvinceChart
            groups={regionGroups}
            total={grandTotal}
            eventName={eventName}
            accentColour="#2563eb"
            label="Attendance by Region & Province"
          />

          {/* ── Comparative analysis: registered vs attended per province ── */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                  <BarChart2 size={13} className="text-indigo-600" />{" "}
                  Registration vs Attendance by Province
                </p>
                <p className="text-[11px] text-gray-400 mt-0.5">
                  How many registered from each province and what percentage
                  showed up
                </p>
              </div>
              <div className="flex items-center gap-3 text-[11px] flex-wrap">
                {highestRateProvince && (
                  <div className="px-3 py-1.5 bg-green-50 rounded-xl border border-green-100">
                    <span className="text-gray-500">Best attendance: </span>
                    <span className="font-bold text-green-700">
                      {highestRateProvince.provinceLabel}
                    </span>
                    <span className="text-green-600 ml-1">
                      {highestRateProvince.attendanceRate}%
                    </span>
                  </div>
                )}
                <div className="px-3 py-1.5 bg-blue-50 rounded-xl border border-blue-100">
                  <span className="text-gray-500">Overall: </span>
                  <span className="font-bold text-blue-700">
                    {overallRate}%
                  </span>
                  <span className="text-gray-400 ml-1">attendance rate</span>
                </div>
              </div>
            </div>

            {/* Sort controls */}
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-gray-400 uppercase tracking-widest mr-1">
                Sort by
              </span>
              {(
                [
                  { key: "registered", label: "Most registered" },
                  { key: "attended", label: "Most attended" },
                  { key: "rate", label: "Attendance rate" },
                ] as { key: typeof compSort; label: string }[]
              ).map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setCompSort(opt.key)}
                  className={`text-[11px] font-semibold px-3 py-1 rounded-lg transition-colors ${compSort === opt.key ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {sortedComparison.length === 0 ? (
              <div className="h-24 flex items-center justify-center text-sm text-gray-300">
                No comparison data yet
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-gray-100">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="px-4 py-2.5 text-left text-[10px] font-bold uppercase tracking-widest text-gray-400">
                        Province
                      </th>
                      <th className="px-4 py-2.5 text-left text-[10px] font-bold uppercase tracking-widest text-gray-400">
                        Region
                      </th>
                      <th className="px-4 py-2.5 text-right text-[10px] font-bold uppercase tracking-widest text-gray-400">
                        Registered
                      </th>
                      <th className="px-4 py-2.5 text-right text-[10px] font-bold uppercase tracking-widest text-gray-400">
                        Attended
                      </th>
                      <th
                        className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest text-gray-400"
                        style={{ minWidth: 160 }}
                      >
                        Attendance Rate
                      </th>
                      <th className="px-4 py-2.5 text-right text-[10px] font-bold uppercase tracking-widest text-gray-400">
                        Cert Sent
                      </th>
                      <th className="px-4 py-2.5 text-right text-[10px] font-bold uppercase tracking-widest text-gray-400">
                        Cert Pending
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {sortedComparison.map((row, idx) => {
                      const colour = rateColour(row.attendanceRate);
                      return (
                        <tr
                          key={idx}
                          className={`hover:bg-gray-50/50 transition-colors ${!row.province ? "bg-gray-50/30" : ""}`}
                        >
                          <td className="px-4 py-2.5 font-medium text-gray-800">
                            {row.province ? (
                              <div className="flex items-center gap-1.5">
                                <Building2
                                  size={10}
                                  className="text-gray-300 shrink-0"
                                />
                                {row.provinceLabel}
                              </div>
                            ) : (
                              <span className="text-gray-400 italic text-[11px]">
                                No province recorded
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-gray-500 text-[11px]">
                            {row.region}
                          </td>
                          <td className="px-4 py-2.5 text-right font-semibold text-gray-700">
                            {row.registered}
                          </td>
                          <td
                            className="px-4 py-2.5 text-right font-bold"
                            style={{ color: colour }}
                          >
                            {row.attended}
                          </td>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all"
                                  style={{
                                    width: `${row.attendanceRate}%`,
                                    background: colour,
                                  }}
                                />
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <span
                                  className="text-[11px] font-bold"
                                  style={{ color: colour }}
                                >
                                  {row.attendanceRate}%
                                </span>
                                <span
                                  className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full"
                                  style={{
                                    background: colour + "18",
                                    color: colour,
                                  }}
                                >
                                  {rateLabel(row.attendanceRate)}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-green-700">
                              <CheckCircle2
                                size={10}
                                className="text-green-500"
                              />
                              {row.certSent}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            {row.certPending > 0 ? (
                              <span className="text-[11px] font-medium text-amber-600">
                                {row.certPending}
                              </span>
                            ) : (
                              <span className="text-[11px] text-gray-300">
                                —
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-gray-200 bg-gray-50">
                      <td
                        className="px-4 py-2.5 font-bold text-gray-900"
                        colSpan={2}
                      >
                        Totals
                      </td>
                      <td className="px-4 py-2.5 text-right font-bold text-gray-900">
                        {comparisonRaw.reduce((s, r) => s + r.registered, 0)}
                      </td>
                      <td className="px-4 py-2.5 text-right font-bold text-blue-700">
                        {comparisonRaw.reduce((s, r) => s + r.attended, 0)}
                      </td>
                      <td className="px-4 py-2.5">
                        <span
                          className="text-[11px] font-bold"
                          style={{ color: rateColour(Number(overallRate)) }}
                        >
                          {overallRate}% overall
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right font-bold text-green-700">
                        {comparisonRaw.reduce((s, r) => s + r.certSent, 0)}
                      </td>
                      <td className="px-4 py-2.5 text-right font-semibold text-amber-600">
                        {comparisonRaw.reduce((s, r) => s + r.certPending, 0)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── FILTERS ── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 space-y-3">
        <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search
              size={13}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search name, email, code…"
              className="w-full pl-8 pr-4 py-2 text-xs rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200 bg-white"
            />
            {search && (
              <button
                onClick={() => {
                  setSearch("");
                  setPage(1);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={12} />
              </button>
            )}
          </div>
          <select
            value={provinceFilter}
            onChange={(e) => {
              setProvinceFilter(e.target.value);
              setPage(1);
            }}
            className="text-xs rounded-xl border border-gray-200 px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 min-w-[150px]"
          >
            <option value="">All provinces</option>
            {allProvinces.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <select
            value={regionFilter}
            onChange={(e) => {
              setRegionFilter(e.target.value);
              setPage(1);
            }}
            className="text-xs rounded-xl border border-gray-200 px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 min-w-[140px]"
          >
            <option value="">All regions</option>
            {allRegions.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <select
            value={certFilter}
            onChange={(e) => {
              setCertFilter(e.target.value);
              setPage(1);
            }}
            className="text-xs rounded-xl border border-gray-200 px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 min-w-[160px]"
          >
            <option value="">All certificate statuses</option>
            <option value="sent">Certificate sent</option>
            <option value="pending">Certificate pending</option>
          </select>
          <div className="flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-2 bg-white min-w-[170px]">
            <ArrowUpDown size={11} className="text-gray-400 shrink-0" />
            <select
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value as SortBy);
                setPage(1);
              }}
              className="text-xs bg-transparent focus:outline-none w-full"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          {hasFilters && (
            <button
              onClick={resetFilters}
              className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-700 px-3 py-2 rounded-xl border border-red-100 hover:bg-red-50 transition-colors"
            >
              <X size={11} /> Clear
            </button>
          )}
        </div>
        <p className="text-[11px] text-gray-400">
          Showing {attendees.length} of {total} attendees
          {provinceFilter && ` · province "${provinceFilter}"`}
          {regionFilter && ` · region "${regionFilter}"`}
          {certFilter &&
            ` · ${certFilter === "sent" ? "certificate sent" : "certificate pending"}`}
        </p>
      </div>

      {/* ── ATTENDEES TABLE ── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-5 space-y-3">
            {[0, 1, 2, 3, 4].map((i) => (
              <Bone key={i} className="h-14" />
            ))}
          </div>
        ) : attendees.length === 0 ? (
          <div className="flex flex-col items-center py-16 text-center gap-2">
            <UserCheck size={24} className="text-gray-300" />
            <p className="text-sm text-gray-500 font-semibold">
              No attendees found
            </p>
            <p className="text-xs text-gray-400">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-blue-50/40">
                  {[
                    "Code",
                    "Name",
                    "Email",
                    "Phone",
                    "Province",
                    "Region",
                    "Certificate",
                    "Registered",
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-widest text-gray-400"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {attendees.map((reg) => (
                  <tr
                    key={reg.id}
                    className="hover:bg-blue-50/20 transition-colors"
                  >
                    <td className="px-4 py-3 text-[11px] font-mono font-bold text-gray-600 whitespace-nowrap">
                      {reg.confirmationCode}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                        {reg.firstName} {reg.lastName}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      {reg.email}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                      {reg.phoneNo ?? "—"}
                    </td>
                    <td className="px-4 py-3">
                      {reg.provinceName ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-700">
                          <Building2
                            size={10}
                            className="text-blue-400 shrink-0"
                          />
                          {reg.provinceName}
                        </span>
                      ) : (
                        <span className="text-[11px] text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-600">
                        <MapPin size={10} className="text-gray-400 shrink-0" />
                        {reg.region}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {reg.hasSentCertificate ? (
                        <span
                          title={
                            reg.certificateSentAt
                              ? `Sent ${fmtDate(reg.certificateSentAt)}`
                              : "Certificate sent"
                          }
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-green-700"
                        >
                          <CheckCircle2
                            size={12}
                            className="text-green-500 shrink-0"
                          />{" "}
                          Sent
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-600">
                          <Clock
                            size={11}
                            className="text-amber-400 shrink-0"
                          />{" "}
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[11px] text-gray-400 whitespace-nowrap">
                      {fmtDate(reg.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between gap-3">
            <p className="text-[11px] text-gray-400">
              Page {page} of {totalPages} · {total} total
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1 || isFetching}
                className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                <ChevronLeft size={14} />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const sp = Math.max(
                  1,
                  Math.min(page - 2, Math.max(1, totalPages - 4)),
                );
                const pg = sp + i;
                if (pg > totalPages) return null;
                return (
                  <button
                    key={pg}
                    onClick={() => setPage(pg)}
                    className={`w-8 h-8 rounded-lg text-xs font-semibold transition-colors ${pg === page ? "bg-blue-600 text-white" : "border border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                  >
                    {pg}
                  </button>
                );
              })}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages || isFetching}
                className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   REGISTRATIONS TAB — original implementation preserved exactly
   as in the single-component version, including eventName-based
   queries, cleanedProvinceChart fallback, and GroupedProvinceChart.
───────────────────────────────────────────────────────────────── */

const RegistrationsTab: React.FC<{
  eventSlug: string;
  eventName: string;
  isAdmin: boolean;
  activeJobId: string | null;
  sendStarting: boolean;
  jobDone: boolean;
  setActiveJobId: (id: string | null) => void;
  setJobDone: (v: boolean) => void;
  onSendCertificates: () => void;
}> = ({
  eventSlug,
  eventName,
  isAdmin,
  activeJobId,
  sendStarting,
  jobDone,
  setActiveJobId,
  setJobDone,
  onSendCertificates,
}) => {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [regionFilter, setRegionFilter] = useState("");
  const [provinceFilter, setProvinceFilter] = useState("");
  const [sortBy, setSortBy] = useState<SortBy>("province");
  const [page, setPage] = useState(1);
  const [showCharts, setShowCharts] = useState(true);
  const [exporting, setExporting] = useState<"excel" | "pdf" | null>(null);
  const [certError, setCertError] = useState<string | null>(null);

  const { data: attendedRes } = useGetEventRegistrationsQuery({
    eventSlug,
    status: "attended",
    limit: 1,
    page: 1,
  });
  const { data: pendingRes } = useGetEventRegistrationsQuery({
    eventSlug,
    status: "attended",
    hasSentCertificate: false,
    limit: 1,
    page: 1,
  });
  const attendedCount = attendedRes?.total ?? 0;
  const pendingCount = pendingRes?.total ?? attendedCount;

  const {
    data: regsRes,
    isLoading: regsLoading,
    isFetching: regsFetching,
    refetch,
  } = useGetEventRegistrationsQuery({
    eventSlug,
    status: statusFilter || undefined,
    regionName: regionFilter || undefined,
    provinceName: provinceFilter || undefined,
    search: search.trim() || undefined,
    sortBy,
    page,
    limit: 50,
  });

  const [updateStatus] = useUpdateRegistrationStatusMutation();
  const [deleteReg] = useDeleteRegistrationMutation();

  const registrations = regsRes?.data ?? [];
  const total = regsRes?.total ?? 0;
  const totalPages = regsRes?.pages ?? 1;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const regionChart = regsRes?.charts?.byRegion ?? [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const provinceChart = regsRes?.charts?.byProvince ?? [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const statusChart = regsRes?.charts?.byStatus ?? [];

  const { data: allRegsRes } = useGetEventRegistrationsQuery({
    eventSlug,
    sortBy: "province",
    limit: 10_000,
    page: 1,
  });
  const { data: chartOnlyRes } = useGetEventRegistrationsQuery({
    eventSlug,
    limit: 1,
    page: 1,
  });
  const allRegistrations = allRegsRes?.data ?? [];
  const grandTotal = allRegsRes?.total ?? total;
  const fullProvinceChart = chartOnlyRes?.charts?.byProvince ?? provinceChart;

  const regionGroups = useMemo(
    () => buildRegionGroups(fullProvinceChart),
    [fullProvinceChart],
  );
  const statsByStatus = useMemo(() => {
    const m: Record<string, number> = {};
    statusChart.forEach((s) => {
      m[s.status] = s.count;
    });
    return m;
  }, [statusChart]);
  const allRegions = useMemo(
    () => Array.from(new Set(regionChart.map((r) => r.region))).sort(),
    [regionChart],
  );
  const allProvinces = useMemo(
    () =>
      Array.from(
        new Set(provinceChart.map((p) => p.province).filter(Boolean)),
      ).sort(),
    [provinceChart],
  );
  const hasFilters = !!(
    statusFilter ||
    regionFilter ||
    provinceFilter ||
    search
  );
  const resetFilters = () => {
    setSearch("");
    setStatusFilter("");
    setRegionFilter("");
    setProvinceFilter("");
    setPage(1);
  };

  const handleStatusChange = async (regId: string, status: string) => {
    try {
      await updateStatus({ regId, status, eventSlug }).unwrap();
    } catch (e) {
      console.error("Status update failed:", e);
    }
  };
  const handleDelete = async (regId: string) => {
    if (!window.confirm("Remove this registration?")) return;
    try {
      await deleteReg({ regId, eventSlug }).unwrap();
    } catch (e) {
      console.error("Delete failed:", e);
    }
  };
  const handleExport = async (format: "excel" | "pdf") => {
    setExporting(format);
    try {
      if (format === "excel")
        await exportRegistrationsToExcel(allRegistrations, eventName);
      else
        await exportFullToPDF(
          allRegistrations,
          eventName,
          regionGroups,
          grandTotal,
        );
    } catch (e) {
      console.error("Export failed:", e);
      alert("Export failed. Make sure xlsx and jspdf packages are installed.");
    } finally {
      setExporting(null);
    }
  };

  // Regions without a province show the region name as their province label
  const cleanedProvinceChart = provinceChart.map((p) => ({
    ...p,
    province: !p.province || p.province === "Unknown" ? p.region : p.province,
  }));

  return (
    <div className="space-y-6">
      {/* Action row */}
      <div className="flex items-center gap-2 flex-wrap justify-end">
        <button
          onClick={() => handleExport("excel")}
          disabled={!!exporting || allRegistrations.length === 0}
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl border border-green-200 text-green-700 hover:bg-green-50 disabled:opacity-50 transition-colors"
        >
          {exporting === "excel" ? (
            <Loader2 size={12} className="animate-spin" />
          ) : (
            <FileSpreadsheet size={12} />
          )}{" "}
          Export Excel
        </button>
        <button
          onClick={() => handleExport("pdf")}
          disabled={!!exporting || allRegistrations.length === 0}
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50 transition-colors"
        >
          {exporting === "pdf" ? (
            <Loader2 size={12} className="animate-spin" />
          ) : (
            <FileText size={12} />
          )}{" "}
          Export PDF
        </button>
        {isAdmin && (
          <button
            onClick={onSendCertificates}
            disabled={sendStarting || !!activeJobId || pendingCount === 0}
            title={
              pendingCount === 0 && attendedCount > 0
                ? "All attended have received certificates"
                : pendingCount === 0
                  ? "No attendees yet"
                  : `Send to ${pendingCount} pending`
            }
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl bg-green-700 hover:bg-green-600 active:scale-[0.98] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-white transition-all"
          >
            {sendStarting ? (
              <>
                <Loader2 size={12} className="animate-spin" /> Starting…
              </>
            ) : activeJobId ? (
              <>
                <Loader2 size={12} className="animate-spin" /> Sending…
              </>
            ) : (
              <>
                <Mail size={12} /> Send Certificates
                {pendingCount > 0 ? ` (${pendingCount})` : ""}
              </>
            )}
          </button>
        )}
        <button
          onClick={() => refetch()}
          className="p-2 rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
        >
          {regsFetching ? (
            <Loader2 size={13} className="animate-spin" />
          ) : (
            <RefreshCw size={13} />
          )}
        </button>
      </div>

      {certError && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-2xl p-4">
          <AlertCircle size={15} className="text-red-600 shrink-0 mt-0.5" />
          <p className="text-sm text-red-700 flex-1">{certError}</p>
          <button
            onClick={() => setCertError(null)}
            className="shrink-0 text-red-400 hover:text-red-600"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {activeJobId && (
        <CertificateProgressBanner
          eventSlug={eventSlug}
          jobId={activeJobId}
          onDone={() => setJobDone(true)}
          onDismiss={() => {
            setActiveJobId(null);
            setJobDone(false);
          }}
        />
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard
          label="Total registered"
          value={grandTotal.toLocaleString()}
          colour="#166534"
          icon={<Users size={14} />}
        />
        <StatCard
          label="Attended"
          value={(statsByStatus["attended"] ?? 0).toLocaleString()}
          sub="confirmed present"
          colour="#2563eb"
          icon={<CheckCircle2 size={14} />}
        />
        <StatCard
          label="Waitlisted"
          value={(statsByStatus["waitlisted"] ?? 0).toLocaleString()}
          colour="#d97706"
          icon={<Clock size={14} />}
        />
        <StatCard
          label="Provinces"
          value={provinceChart.length}
          sub="represented"
          colour="#7c3aed"
          icon={<Building2 size={14} />}
        />
      </div>

      {/* Analytics toggle */}
      <div className="flex items-center justify-between">
        <h2 className="text-xs font-bold uppercase tracking-widest text-gray-400">
          Registration analytics
        </h2>
        <button
          onClick={() => setShowCharts((v) => !v)}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-gray-800 transition-colors"
        >
          <BarChart2 size={13} />
          {showCharts ? "Hide charts" : "Show charts"}
        </button>
      </div>

      {showCharts && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Province bar chart */}
            <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <p className="text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                <Building2 size={13} className="text-purple-600" /> Distribution
                by Province
              </p>
              <p className="text-[11px] text-gray-400 mb-4">
                {provinceChart.length} province
                {provinceChart.length !== 1 ? "s" : ""} represented
              </p>
              {provinceChart.length === 0 ? (
                <div className="h-48 flex items-center justify-center text-sm text-gray-300">
                  No province data yet
                </div>
              ) : (
                <ResponsiveContainer
                  width="100%"
                  height={Math.max(200, provinceChart.length * 28)}
                >
                  <BarChart data={cleanedProvinceChart} layout="vertical">
                    <CartesianGrid
                      strokeDasharray="3 3"
                      horizontal={false}
                      stroke="#f3f4f6"
                    />
                    <XAxis
                      type="number"
                      tick={{ fontSize: 10 }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="province"
                      width={110}
                      tick={{ fontSize: 10 }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        borderRadius: 10,
                        border: "1px solid #e5e7eb",
                        fontSize: 12,
                      }}
                      formatter={(v, _n, props) => [
                        `${Number(v)} registered`,
                        `${props.payload?.province || props.payload?.region}`,
                      ]}
                    />
                    <Bar
                      dataKey="count"
                      radius={[0, 6, 6, 0]}
                      maxBarSize={20}
                      label={{
                        position: "right",
                        fontSize: 10,
                        fill: "#6b7280",
                      }}
                    >
                      {provinceChart.map((_, i) => (
                        <Cell key={i} fill={COLOURS[i % COLOURS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="flex flex-col gap-5">
              {/* Status pie */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <p className="text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                  <BarChart2 size={13} className="text-blue-600" /> Status
                  Breakdown
                </p>
                {statusChart.length === 0 ? (
                  <div className="h-32 flex items-center justify-center text-sm text-gray-300">
                    No data yet
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie
                        data={statusChart}
                        dataKey="count"
                        nameKey="status"
                        cx="50%"
                        cy="50%"
                        outerRadius={60}
                        label={({ name, percent }) =>
                          `${name ?? ""} ${((percent ?? 0) * 100).toFixed(0)}%`
                        }
                        labelLine={false}
                      >
                        {statusChart.map((entry, i) => (
                          <Cell
                            key={i}
                            fill={
                              STATUS_COLOURS[entry.status] ??
                              COLOURS[i % COLOURS.length]
                            }
                          />
                        ))}
                      </Pie>
                      <Legend
                        iconType="circle"
                        iconSize={8}
                        formatter={(v) => (
                          <span
                            style={{
                              fontSize: 11,
                              color: "#6b7280",
                              textTransform: "capitalize",
                            }}
                          >
                            {v}
                          </span>
                        )}
                      />
                      <Tooltip
                        contentStyle={{
                          borderRadius: 10,
                          border: "1px solid #e5e7eb",
                          fontSize: 12,
                        }}
                        formatter={(v, name) => [
                          `${Number(v)} registrants`,
                          String(name),
                        ]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Region mini bars */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <p className="text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                  <TrendingUp size={13} className="text-green-600" /> By Region
                </p>
                <p className="text-[11px] text-gray-400 mb-3">
                  {regionChart.length} region
                  {regionChart.length !== 1 ? "s" : ""}
                </p>
                {regionChart.length === 0 ? (
                  <div className="h-24 flex items-center justify-center text-sm text-gray-300">
                    No data yet
                  </div>
                ) : (
                  <div className="space-y-2">
                    {regionChart.slice(0, 8).map((r, i) => {
                      const max = regionChart[0]?.count ?? 1;
                      return (
                        <div key={r.region} className="flex items-center gap-2">
                          <p className="text-[10px] text-gray-600 w-20 truncate shrink-0">
                            {r.region}
                          </p>
                          <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                            <div
                              className="h-full rounded-full"
                              style={{
                                width: `${(r.count / max) * 100}%`,
                                background: COLOURS[i % COLOURS.length],
                              }}
                            />
                          </div>
                          <p className="text-[10px] text-gray-500 shrink-0 w-6 text-right">
                            {r.count}
                          </p>
                        </div>
                      );
                    })}
                    {regionChart.length > 8 && (
                      <p className="text-[10px] text-gray-400">
                        +{regionChart.length - 8} more regions
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <GroupedProvinceChart
            groups={regionGroups}
            total={grandTotal}
            eventName={eventName}
          />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 space-y-3">
        <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search
              size={13}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search name, email, province, code…"
              className="w-full pl-8 pr-4 py-2 text-xs rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-200 bg-white"
            />
            {search && (
              <button
                onClick={() => {
                  setSearch("");
                  setPage(1);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={12} />
              </button>
            )}
          </div>
          <select
            value={provinceFilter}
            onChange={(e) => {
              setProvinceFilter(e.target.value);
              setPage(1);
            }}
            className="text-xs rounded-xl border border-gray-200 px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-green-200 min-w-[150px]"
          >
            <option value="">All provinces</option>
            {allProvinces.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <select
            value={regionFilter}
            onChange={(e) => {
              setRegionFilter(e.target.value);
              setPage(1);
            }}
            className="text-xs rounded-xl border border-gray-200 px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-green-200 min-w-[140px]"
          >
            <option value="">All regions</option>
            {allRegions.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="text-xs rounded-xl border border-gray-200 px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-green-200 min-w-[130px]"
          >
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s} className="capitalize">
                {s}
              </option>
            ))}
          </select>
          <div className="flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-2 bg-white min-w-[170px]">
            <ArrowUpDown size={11} className="text-gray-400 shrink-0" />
            <select
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value as SortBy);
                setPage(1);
              }}
              className="text-xs bg-transparent focus:outline-none w-full"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          {hasFilters && (
            <button
              onClick={resetFilters}
              className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-700 px-3 py-2 rounded-xl border border-red-100 hover:bg-red-50 transition-colors"
            >
              <X size={11} /> Clear
            </button>
          )}
        </div>
        <p className="text-[11px] text-gray-400">
          Showing {registrations.length} of {total} registrations
          {provinceFilter && ` · province "${provinceFilter}"`}
          {regionFilter && ` · region "${regionFilter}"`}
          {statusFilter && ` · status "${statusFilter}"`}
        </p>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {regsLoading ? (
          <div className="p-5 space-y-3">
            {[0, 1, 2, 3, 4].map((i) => (
              <Bone key={i} className="h-14" />
            ))}
          </div>
        ) : registrations.length === 0 ? (
          <div className="flex flex-col items-center py-16 text-center gap-2">
            <Users size={24} className="text-gray-300" />
            <p className="text-sm text-gray-500 font-semibold">
              No registrations found
            </p>
            <p className="text-xs text-gray-400">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  {[
                    "Code",
                    "Name",
                    "Email",
                    "Phone",
                    "Province",
                    "Region",
                    "Status",
                    "Certificate",
                    "Registered",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-widest text-gray-400"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {registrations.map((reg) => (
                  <tr
                    key={reg.id}
                    className="hover:bg-gray-50/50 transition-colors group"
                  >
                    <td className="px-4 py-3 text-[11px] font-mono font-bold text-gray-600 whitespace-nowrap">
                      {reg.confirmationCode}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                        {reg.firstName} {reg.lastName}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      {reg.email}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                      {reg.phoneNo ?? "—"}
                    </td>
                    <td className="px-4 py-3">
                      {reg.provinceName ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-purple-700">
                          <Building2
                            size={10}
                            className="text-purple-400 shrink-0"
                          />
                          {reg.provinceName}
                        </span>
                      ) : (
                        <span className="text-[11px] text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-600">
                        <MapPin size={10} className="text-gray-400 shrink-0" />
                        {reg.region}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={reg.status}
                        onChange={(e) =>
                          handleStatusChange(reg.id, e.target.value)
                        }
                        className="text-[11px] font-bold rounded-lg border-0 bg-transparent cursor-pointer focus:ring-2 focus:ring-green-200 focus:outline-none p-0"
                        style={{
                          color: STATUS_COLOURS[reg.status] ?? "#6b7280",
                        }}
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s} className="capitalize">
                            {s}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      {reg.hasSentCertificate ? (
                        <span
                          title={
                            reg.certificateSentAt
                              ? `Sent ${fmtDate(reg.certificateSentAt)}`
                              : "Certificate sent"
                          }
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-green-700"
                        >
                          <CheckCircle2
                            size={12}
                            className="text-green-500 shrink-0"
                          />
                          Sent
                        </span>
                      ) : (
                        <span className="text-[11px] text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[11px] text-gray-400 whitespace-nowrap">
                      {fmtDate(reg.createdAt)}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleDelete(reg.id)}
                        className="p-1.5 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                        title="Remove registration"
                      >
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between gap-3">
            <p className="text-[11px] text-gray-400">
              Page {page} of {totalPages} · {total} total
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1 || regsFetching}
                className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                <ChevronLeft size={14} />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const sp = Math.max(
                  1,
                  Math.min(page - 2, Math.max(1, totalPages - 4)),
                );
                const pg = sp + i;
                if (pg > totalPages) return null;
                return (
                  <button
                    key={pg}
                    onClick={() => setPage(pg)}
                    className={`w-8 h-8 rounded-lg text-xs font-semibold transition-colors ${pg === page ? "bg-green-600 text-white" : "border border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                  >
                    {pg}
                  </button>
                );
              })}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages || regsFetching}
                className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   MAIN COMPONENT — tab shell
───────────────────────────────────────────────────────────────── */

const ViewEventRegister: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAppSelector((s) => s.auth);
  const isAdmin = user?.role === "admin";

  const event = staticEvent;
  const eventSlug = (event as any).slug ?? "rccg-c2-yaya-leadership-programme";

  // Shared certificate send state — lifted so both tabs can trigger it
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [jobDone, setJobDone] = useState(false);
  const [sendStarting, setSendStarting] = useState(false);
  const [activeTab, setActiveTab] = useState<"registrations" | "attendees">(
    "registrations",
  );

  // Tab badge counts
  const { data: summaryRes } = useGetEventRegistrationsQuery({
    eventSlug,
    limit: 1,
    page: 1,
  });
  const { data: attendedSummary } = useGetEventAttendeesQuery({
    eventSlug,
    limit: 1,
    page: 1,
  });
  const totalRegistered = summaryRes?.total ?? 0;
  const totalAttended = attendedSummary?.totalAttended ?? 0;

  const [sendCertificates] = useSendCertificatesMutation();

  const handleSendCertificates = async () => {
    if (!isAdmin) return;
    const certPending =
      (attendedSummary?.totalAttended ?? 0) -
      (attendedSummary?.certificateSentCount ?? 0);
    if (certPending === 0) return;
    const confirmed = window.confirm(
      `Send certificates to ${certPending} pending attendee${certPending !== 1 ? "s" : ""}?\n\nThis will start a background job and email each person their personalised PDF certificate.`,
    );
    if (!confirmed) return;
    setSendStarting(true);
    setJobDone(false);
    setActiveJobId(null);
    try {
      const result = await sendCertificates({ eventSlug }).unwrap();
      setActiveJobId(result.jobId);
    } catch (err: any) {
      alert(
        err?.data?.error ??
          "Failed to start certificate job. Please try again.",
      );
    } finally {
      setSendStarting(false);
    }
  };

  const TABS = [
    {
      id: "registrations" as const,
      label: "All Registrations",
      count: totalRegistered,
      colour: "green",
    },
    {
      id: "attendees" as const,
      label: "Attendees",
      count: totalAttended,
      colour: "blue",
    },
  ];

  return (
    <div className="space-y-6 pb-12">
      {/* ── HEADER ── */}
      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 transition-colors shrink-0"
        >
          <ChevronLeft size={14} /> Back to events
        </button>
        <div className="flex-1">
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-green-600 mb-1">
            Event Management
          </p>
          <h1 className="text-2xl font-bold text-gray-900 leading-tight">
            {event.name}
          </h1>
          <p className="text-xs text-gray-400 mt-1">
            {new Date(event.eventDate).toLocaleDateString("en-GB", {
              weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
            {event.venue ? ` · ${event.venue}` : ""}
          </p>
        </div>
      </div>

      {/* ── TABS ── */}
      <div className="flex items-center gap-1 border-b border-gray-100">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-all -mb-px ${
              activeTab === tab.id
                ? tab.colour === "blue"
                  ? "border-blue-600 text-blue-700"
                  : "border-green-600 text-green-700"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span
                className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
                  activeTab === tab.id
                    ? tab.colour === "blue"
                      ? "bg-blue-100 text-blue-700"
                      : "bg-green-100 text-green-700"
                    : "bg-gray-100 text-gray-500"
                }`}
              >
                {tab.count.toLocaleString()}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── TAB CONTENT ── */}
      {activeTab === "registrations" ? (
        <RegistrationsTab
          eventSlug={eventSlug}
          eventName={event.name}
          isAdmin={isAdmin}
          activeJobId={activeJobId}
          sendStarting={sendStarting}
          jobDone={jobDone}
          setActiveJobId={setActiveJobId}
          setJobDone={setJobDone}
          onSendCertificates={handleSendCertificates}
        />
      ) : (
        <AttendeesTab
          eventSlug={eventSlug}
          eventName={event.name}
          isAdmin={isAdmin}
          activeJobId={activeJobId}
          sendStarting={sendStarting}
          onSendCertificates={handleSendCertificates}
        />
      )}
    </div>
  );
};

export default ViewEventRegister;
